import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useForm, useSession } from "hooks";
import { BuildingId, DeviceConfig, DeviceConfigAudioType, DeviceConfigId, DeviceFamily, DeviceType, Protocol, ProtocolId, ServiceMessageSet, ServiceMessageSetId, VoiceSet, VoiceSetId, VolumeType } from 'shared/models';
import { TabContainer } from "ui/shared/containers";
import { TextField } from "ui/shared/controls";
import { ErrorMessage } from "ui/shared/messages";
import { ConfirmationDialog } from "ui/shell/confirmationDialog/ConfirmationDialog";
import { BasicInfoTab } from "./basicInfoTab/BasicInfoTab";
import { ServiceMessagesTab } from "./serviceMessagesTab/ServiceMessagesTab";
import { AudioTab } from "./audioTab/AudioTab";
import UUID from "util/UUID";

require('./DeviceConfigForm.css');

export type Props = {
    config: DeviceConfig | null;
    readOnly?: boolean;
    protocols: Record<ProtocolId, Protocol>;
    serviceMessageSets: Record<ServiceMessageSetId, ServiceMessageSet>;
    voiceSets: Record<VoiceSetId, VoiceSet>;
    buildingId: BuildingId;
    deviceType: DeviceType | null;
    isPI: boolean;
    busy: boolean;
    canDelete: boolean;
    errorKey: string | null;
    onSave(deviceConfig: DeviceConfig, asNew: boolean, closeDialog: boolean): Promise<DeviceConfig | null>;
    onDelete(deviceConfigId: DeviceConfigId): void;
    onCancel(): void;
};

const nullValue = 'none';
const deleteOnValue = 'default-' + UUID.generate();

export const DeviceConfigForm = ({
    config, readOnly = false, busy, canDelete, errorKey, onSave, onDelete, onCancel, protocols, serviceMessageSets, voiceSets, buildingId, deviceType, isPI,
}: Props) => {
    const { t } = useTranslation();
    const { langCode } = useSession();

    const initialData = useMemo<Partial<DeviceConfig>>(() => {
        const configPropsByDeviceType = deviceType?.deviceFamily === DeviceFamily.Giotto
            ? {
                [VolumeType.Background]: 0,
                [VolumeType.Buzzer]: 5,
                [VolumeType.Chime]: 5,
                [VolumeType.Direction]: 0,
                [VolumeType.Floor]: 5,
                [VolumeType.FloorPassing]: 5,
                [VolumeType.ServiceMessage]: 5,
                audioOrder: [
                    DeviceConfigAudioType.Floor,
                    DeviceConfigAudioType.Gong,
                    DeviceConfigAudioType.Direction,
                ],
            }
            : {
                [VolumeType.Master]: 2,
                [VolumeType.Media]: 2,
                [VolumeType.Synth]: 2,
                audioOrder: [
                    DeviceConfigAudioType.Floor,
                    DeviceConfigAudioType.Direction,
                    DeviceConfigAudioType.Gong,
                ],
            };
        
        return {
            displayName: '',
            buildingId,
            serviceMessageSetId: null,
            voiceSetId: null,
            ...config,
            properties: {
                deviceFamily: deviceType?.deviceFamily || DeviceFamily.MatisseR5,
                langCode: langCode,
                maxPersons: '',
                maxWeight: '',
                floorOverrides: [],
                serviceMessageOverrides: {},
                voiceOverrides: {},
                params: {},
                ...configPropsByDeviceType,
                ...(config?.properties || {}),
            },
        } as DeviceConfig;
    }, [ deviceType?.deviceFamily, config, buildingId, langCode ]);

    const { data, dirty, setData, handleChange, handleSubmit } = useForm('device-config', initialData as DeviceConfig, data => onSave(data, false, true), { nullValue, deleteOnValue });

    const [ deleteDialogVisible, setDeleteDialogVisible ] = useState(false);

    const handleDelete = useCallback(() => {
        setDeleteDialogVisible(false);
        onDelete(data.id);
    }, [ onDelete, setDeleteDialogVisible, data.id ]);

    const handleAutoSave = useCallback(async (config: DeviceConfig): Promise<DeviceConfigId | null> => {
        const savedConfig = await onSave(config, false, false);
        if (!savedConfig) return null;
        setData(savedConfig);
        return savedConfig.id;
    }, [ setData, onSave ]);

    //
    // Update the form if the input config changes
    //
    useEffect(() => {
        if (config) {
            setData(config);
        }
    }, [ config, setData ]);

    const mr5DeviceTypeCheck = deviceType?.deviceFamily === DeviceFamily.MatisseR5
        || deviceType?.deviceFamily === DeviceFamily.Giotto
        || deviceType?.deviceFamily === DeviceFamily.Raffaello
        || deviceType?.deviceFamily === DeviceFamily.MatisseFlexR2;

    const tabLabels = useMemo(() => mr5DeviceTypeCheck ? [
        t('DEVICE_CONFIGS.DIALOG.TABS.BASIC_CONFIG'),
        t('DEVICE_CONFIGS.DIALOG.TABS.SERVICE_MESSAGES'),
        t('DEVICE_CONFIGS.DIALOG.TABS.AUDIO'),
    ] : [ t('DEVICE_CONFIGS.DIALOG.TABS.BASIC_CONFIG') ], [ t, mr5DeviceTypeCheck ]);

    const canSaveAsNew = !!config?.id && config?.displayName?.trim() !== data.displayName.trim();
    return (
        <form
            onSubmit={ handleSubmit }
            id="device-config-form"
            className={ `ui form${errorKey ? ' _error' : ''}${busy ? ' loading' : ''}` }
        >
            <TextField
                id="device-config-display-name"
                className="_name"
                name="displayName"
                labelKey="LABEL.CONFIGURATION_NAME"
                required={ true }
                maxLength={ 32 }
                autoComplete="off"
                readOnly={ readOnly }
                value={ data.displayName }
                onChange={ handleChange }
            />

            <TabContainer
                id="device-config-tabs"
                className="_tabs"
                tabLabels={ tabLabels }
            >
                <BasicInfoTab
                    config={ data }
                    protocols={ protocols }
                    deviceFamily={ deviceType?.deviceFamily || null }
                    readOnly={ readOnly }
                    onChange={ handleChange }
                    setData={ setData }
                />

                { mr5DeviceTypeCheck &&
                    <ServiceMessagesTab
                        buildingId={ buildingId }
                        readOnly={ readOnly }
                        deviceType={ deviceType }
                        isPI={ isPI }
                        config={ data }
                        protocols={ protocols }
                        serviceMessageSets={ serviceMessageSets }
                        onChange={ handleChange }
                        onAutoSave={ handleAutoSave }
                        setData={ setData }
                    />
                }
                
                { mr5DeviceTypeCheck &&
                    <AudioTab
                        config={ data }
                        readOnly={ readOnly }
                        voiceSets={ voiceSets }
                        deleteOnValue={ deleteOnValue }
                        onChange={ handleChange }
                        setData={ setData }
                    />
                }

            </TabContainer>
            
            <div className="_buttons">
                { dirty && !readOnly &&
                    <button
                        className="ui primary submit button _save"
                        type="submit"
                        disabled={ busy }
                    >
                        { t('COMMAND.SAVE') }
                    </button>
                }
                { canSaveAsNew && !readOnly &&
                    <button
                        className="ui button _save-new"
                        type="button"
                        disabled={ busy }
                        onClick={ () => onSave(data, true, true) }
                    >
                        { t('COMMAND.SAVE_AS_NEW') }
                    </button>
                }
                <button
                    className="ui button _cancel"
                    type="button"
                    disabled={ busy }
                    onClick={ onCancel }
                >
                    { t(dirty || !config ? 'COMMAND.CANCEL' : 'COMMAND.CLOSE') }
                </button>
                { canDelete && !!config && !readOnly &&
                    <button
                        className="ui red basic button _delete"
                        type="button"
                        disabled={ busy }
                        onClick={ () => setDeleteDialogVisible(true) }
                    >
                        { t('COMMAND.DELETE') }
                    </button>
                }
            </div>

            <ErrorMessage
                className="_error"
                messageKey={ errorKey }
            />
    
            <ConfirmationDialog
                id="delete-device-config-confirmation-dialog"
                message={ t('LABEL.CONFIRM_DELETE', [ config?.displayName || t('NO_NAME') ]) }
                visible={ deleteDialogVisible }
                onClickNo={ () => setDeleteDialogVisible(false) }
                onClickYes={ handleDelete }
            />
        </form>
    );
};
